import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import { listReviewItems, recordDecision } from "@/api/reviewItems";
import type { DecisionVerb, ReviewItem } from "@/api/types";
import { useToast } from "@/components/toast";
import { getUsername } from "@/auth/roles";
import { itemTypeLabel, langTagLabel } from "./labels";
import {
  LlmSuggestionPanel,
  type AppliedAlternativeSelection,
} from "./components/LlmSuggestionPanel";
import { RecordFormSwitch } from "./components/recordForms/RecordFormSwitch";
import {
  fieldOverridesForItem,
  type PayloadShape,
} from "./components/recordForms/shared";

const HOTKEYS: Record<string, DecisionVerb> = {
  a: "approve",
  e: "edit_and_approve",
  r: "reject",
  n: "needs_second_review",
  s: "skip",
};

interface PegEntry {
  stable_key: string;
  review_item_id?: string;
  decision: DecisionVerb;
  source_text: string;
  ts: number;
}

// PayloadShape — импортируется из ./components/recordForms/shared.ts

export default function AdminReviewBatch() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  const [cursor, setCursor] = useState(0);
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [note, setNote] = useState("");
  const [noteOpen, setNoteOpen] = useState(false);
  const [pegs, setPegs] = useState<PegEntry[]>([]);
  // edit_and_approve flow — двухэтапный, как в Focus.
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [pendingSuggestion, setPendingSuggestion] = useState<{
    suggestion_id: string;
    applied_alternative_index: number;
  } | null>(null);

  // Сессионная статистика — счётчики в этой пробеге.
  const [sessionStats, setSessionStats] = useState({
    approved: 0,
    rejected: 0,
    skipped: 0,
  });

  const queueQuery = useQuery({
    queryKey: ["review-items", "batch-queue", params.toString()],
    queryFn: async () => {
      const r = await listReviewItems({
        status: params.get("status") ?? "open",
        item_type: params.get("item_type") ?? undefined,
        domain: params.get("domain") ?? undefined,
        limit: 200,
      });
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
  });

  const queue = queueQuery.data?.items ?? [];
  const total = queue.length;
  const item = cursor < queue.length ? queue[cursor] : null;
  const reviewer =
    (auth.user?.profile?.preferred_username as string | undefined) ??
    (auth.user?.profile?.email as string | undefined) ??
    "anonymous";

  // Сброс edit-state при смене item.
  useEffect(() => {
    setEditMode(false);
    setDraft({});
    setPendingSuggestion(null);
  }, [item?.review_item_id, item?.stable_key]);

  const decideMutation = useMutation({
    mutationFn: async (input: {
      decision: DecisionVerb;
      after_payload?: Record<string, unknown>;
    }) => {
      if (!item) throw new Error("нет текущего item");
      const r = await recordDecision(item.stable_key, {
        decision: input.decision,
        reviewer,
        reviewer_note: note || undefined,
        after_payload: input.after_payload,
      });
      if (!r.ok) throw new Error(r.error);
      return { ...r.data, decision: input.decision };
    },
    onSuccess: (data) => {
      toast(`${data.decision}`, "success");
      if (item) {
        setPegs((prev) =>
          [
            {
              stable_key: item.stable_key,
              review_item_id: item.review_item_id,
              decision: data.decision,
              source_text: item.source_text ?? item.candidate_text ?? "—",
              ts: Date.now(),
            },
            ...prev,
          ].slice(0, 5),
        );
      }
      setSessionStats((s) => ({
        approved:
          s.approved +
          (data.decision === "approve" || data.decision === "edit_and_approve"
            ? 1
            : 0),
        rejected: s.rejected + (data.decision === "reject" ? 1 : 0),
        skipped:
          s.skipped +
          (data.decision === "skip" ||
          data.decision === "needs_second_review" ||
          data.decision === "needs_review"
            ? 1
            : 0),
      }));
      setNote("");
      setNoteOpen(false);
      setEditMode(false);
      setDraft({});
      setPendingSuggestion(null);
      void qc.invalidateQueries({ queryKey: ["review-items"] });
      void qc.invalidateQueries({
        queryKey: ["llm-suggestions", item?.stable_key],
      });
      if (autoAdvance) setCursor((c) => c + 1);
    },
    onError: (err: Error) => toast(`Не применено: ${err.message}`, "error"),
  });

  /** Двухэтапный edit_and_approve, как в Focus. */
  const handleDecision = (decision: DecisionVerb): void => {
    if (decision === "edit_and_approve") {
      if (!editMode) {
        setEditMode(true);
        toast(
          "Поля разблокированы — нажми «Подтвердить» (E ещё раз) для отправки",
          "info",
        );
        return;
      }
      const baseDraft: Record<string, unknown> =
        Object.keys(draft).length > 0 ? { ...draft } : {};
      if (pendingSuggestion) {
        baseDraft.llm_suggestion_id = pendingSuggestion.suggestion_id;
        baseDraft.applied_alternative_index =
          pendingSuggestion.applied_alternative_index;
      }
      decideMutation.mutate({
        decision: "edit_and_approve",
        after_payload:
          Object.keys(baseDraft).length > 0 ? baseDraft : undefined,
      });
      return;
    }
    if (editMode) {
      setEditMode(false);
      setDraft({});
      setPendingSuggestion(null);
    }
    decideMutation.mutate({ decision });
  };

  const handleApplyLlm = (sel: AppliedAlternativeSelection): void => {
    if (!item) return;
    const overrides = fieldOverridesForItem(item, sel.alternative.target_text);
    setDraft((prev) => ({ ...prev, ...overrides }));
    setEditMode(true);
    setPendingSuggestion({
      suggestion_id: sel.suggestion.suggestion_id,
      applied_alternative_index: sel.alternative.index,
    });
    toast(
      `Применили A${sel.alternative.index} — нажми E для подтверждения`,
      "info",
    );
  };

  // Hot-keys
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (
        ev.target instanceof HTMLElement &&
        (ev.target.tagName === "INPUT" || ev.target.tagName === "TEXTAREA")
      ) {
        return;
      }
      if (ev.metaKey || ev.ctrlKey || ev.altKey) return;
      const verb = HOTKEYS[ev.key.toLowerCase()];
      if (verb) {
        ev.preventDefault();
        if (item) handleDecision(verb);
        return;
      }
      if (ev.key === "j") {
        ev.preventDefault();
        setCursor((c) => Math.min(c + 1, queue.length));
      }
      if (ev.key === "k") {
        ev.preventDefault();
        setCursor((c) => Math.max(c - 1, 0));
      }
      if (ev.key === "Escape") {
        ev.preventDefault();
        if (editMode) {
          setEditMode(false);
          setDraft({});
          setPendingSuggestion(null);
          return;
        }
        navigate(`/admin/review?${params.toString()}`);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queue.length,
    navigate,
    params,
    item,
    editMode,
    draft,
    pendingSuggestion,
  ]);

  if (queueQuery.isLoading) {
    return <div className="mt-empty">Загружаем очередь…</div>;
  }
  if (queueQuery.isError) {
    return (
      <div className="mt-empty mt-empty--error">
        {(queueQuery.error as Error).message}
      </div>
    );
  }
  if (queue.length === 0) {
    return (
      <div className="mt-empty">
        Очередь пуста.{" "}
        <Link to="/admin/review">Назад к списку</Link>
      </div>
    );
  }
  if (!item) {
    return (
      <div className="rb-shell">
        <div className="rb-strip">
          <Link to="/admin/review" className="mt-btn mt-btn--sm mt-btn--ghost">
            ‹ К очереди
          </Link>
          <span className="rb-strip__counter">
            {total} <em>/ {total}</em>
          </span>
          <div className="rb-strip__bar">
            <i style={{ width: "100%" }} />
          </div>
        </div>
        <main className="rb-stage">
          <div
            className="mt-empty"
            style={{ background: "var(--panel)", maxWidth: 560 }}
          >
            <h2 style={{ margin: "0 0 12px" }}>Готово!</h2>
            <p>
              Просмотрено: <strong>{sessionStats.approved}</strong> ✓ ·{" "}
              <strong>{sessionStats.rejected}</strong> ✗ ·{" "}
              <strong>{sessionStats.skipped}</strong> ⤼
            </p>
            <Link to="/admin/review" className="mt-btn mt-btn--primary">
              К очереди
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="rb-shell">
      {/* PROGRESS STRIP */}
      <div className="rb-strip">
        <span className="rb-strip__view">
          фильтр:{" "}
          <strong>
            {params.get("status") ?? "open"}
            {params.get("item_type") ? ` · ${params.get("item_type")}` : ""}
            {params.get("domain") ? ` · ${params.get("domain")}` : ""}
          </strong>
        </span>
        <div className="mt-divider--v" />
        <span className="rb-strip__counter">
          {cursor + 1} <em>/ {total}</em>
        </span>
        <div className="rb-strip__bar">
          <i style={{ width: `${((cursor + 1) / total) * 100}%` }} />
        </div>
        <div
          className="rb-strip__chips"
          title="Текущая сессия"
        >
          <span className="rb-strip__chip rb-strip__chip--ok">
            ✓ {sessionStats.approved}
          </span>
          <span className="rb-strip__chip rb-strip__chip--no">
            ✗ {sessionStats.rejected}
          </span>
          <span className="rb-strip__chip rb-strip__chip--skip">
            ⤼ {sessionStats.skipped}
          </span>
        </div>
        <span className="mt-toolbar__sep" style={{ flex: 1 }} />
        <span className="mt-text-tight mt-muted">Auto-advance</span>
        <label className="mt-check">
          <input
            type="checkbox"
            checked={autoAdvance}
            onChange={(e) => setAutoAdvance(e.target.checked)}
          />{" "}
          on
        </label>
        <span className="rb-strip__exit">
          <span className="mt-divider--v" />
          <Link to="/admin/review">⏸ Pause &amp; back to list</Link>
        </span>
      </div>

      {/* STAGE */}
      <main className="rb-stage">
        <BatchCard item={item} />

        {/* Inline type-aware форма появляется в edit-mode (E один раз)
             или после Apply LLM-альтернативы. Полное переиспользование
             type-specific форм из Focus. */}
        {editMode && (
          <div style={{ maxWidth: 720, margin: "12px auto 0" }}>
            <RecordFormSwitch
              item={item}
              payload={(item.payload ?? {}) as PayloadShape}
              editMode={editMode}
              draft={draft}
              onChange={(key, value) =>
                setDraft((prev) => ({ ...prev, [key]: value }))
              }
              onCancelEdit={() => {
                setEditMode(false);
                setDraft({});
                setPendingSuggestion(null);
              }}
              onApplyLlm={handleApplyLlm}
            />
          </div>
        )}

        {/* LLM advisor (compact, под hero) */}
        {item.review_item_id && (
          <div style={{ maxWidth: 720, margin: "12px auto 0" }}>
            <LlmSuggestionPanel
              reviewItemId={item.review_item_id}
              onApply={handleApplyLlm}
            />
          </div>
        )}
      </main>

      {/* DOCK */}
      <div className="rb-dock">
        <div className="rb-dock__inner">
          <div className="rb-dock__row">
            <button
              className="mt-decision mt-decision--approve"
              onClick={() => handleDecision("approve")}
              disabled={decideMutation.isPending}
              type="button"
            >
              <span className="mt-decision__key">A</span>Approve
            </button>
            <button
              className="mt-decision mt-decision--edit"
              onClick={() => handleDecision("edit_and_approve")}
              disabled={decideMutation.isPending}
              type="button"
            >
              <span className="mt-decision__key">E</span>Edit
            </button>
            <button
              className="mt-decision mt-decision--reject"
              onClick={() => handleDecision("reject")}
              disabled={decideMutation.isPending}
              type="button"
            >
              <span className="mt-decision__key">R</span>Reject
            </button>
            <button
              className="mt-decision"
              onClick={() => handleDecision("needs_second_review")}
              disabled={decideMutation.isPending}
              type="button"
            >
              <span className="mt-decision__key">N</span>Second review
            </button>
            <button
              className="mt-decision"
              onClick={() => handleDecision("skip")}
              disabled={decideMutation.isPending}
              type="button"
            >
              <span className="mt-decision__key">S</span>Skip
            </button>
          </div>
          {!noteOpen ? (
            <div className="rb-dock__note-toggle">
              <button type="button" onClick={() => setNoteOpen(true)}>
                + Reviewer note
              </button>
              <span className="mt-muted">— опционально, для второго ревьюера</span>
            </div>
          ) : (
            <div
              className="rf-dock__note-row"
              style={{ display: "flex", gap: 6 }}
            >
              <span
                className="mt-text-tight mt-muted"
                style={{ flex: "0 0 auto" }}
              >
                Reviewer note
              </span>
              <input
                className="mt-input mt-input--sm"
                placeholder="comment for next reviewer…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                autoFocus
              />
              <button
                className="mt-icon-btn"
                onClick={() => {
                  setNoteOpen(false);
                  setNote("");
                }}
                title="Hide"
                type="button"
              >
                ×
              </button>
            </div>
          )}
          <div className="rb-dock__hint">
            <span>
              <span className="mt-kbd">A</span> approve
            </span>
            <span className="rb-dock__sep" />
            <span>
              <span className="mt-kbd">E</span> edit
            </span>
            <span className="rb-dock__sep" />
            <span>
              <span className="mt-kbd">R</span> reject
            </span>
            <span className="rb-dock__sep" />
            <span>
              <span className="mt-kbd">N</span> 2nd review
            </span>
            <span className="rb-dock__sep" />
            <span>
              <span className="mt-kbd">S</span> skip
            </span>
            <span className="rb-dock__sep" />
            <span>
              <span className="mt-kbd">J</span>
              <span className="mt-kbd">K</span> next/prev
            </span>
            <span className="rb-dock__sep" />
            <span>
              <span className="mt-kbd">Esc</span> exit
            </span>
          </div>
        </div>
      </div>

      {/* SIDE PEGS — last 5 decisions */}
      {pegs.length > 0 && (
        <div className="rb-pegs">
          <div className="rb-pegs__title">Last 5 · Click для перехода</div>
          {pegs.map((p, i) => (
            <Link
              key={i}
              to={`/admin/review/focus/${encodeURIComponent(p.review_item_id ?? p.stable_key)}`}
              className="rb-peg"
              style={{ textDecoration: "none" }}
            >
              <span className={`rb-peg__icon ${pegIconClass(p.decision)}`}>
                {pegIcon(p.decision)}
              </span>
              <span className="rb-peg__name">
                {p.source_text} · {formatAgo(p.ts)}
              </span>
            </Link>
          ))}
        </div>
      )}
      {/* Hidden username для мини-stats — просто чтобы import не warned */}
      <span style={{ display: "none" }}>{getUsername(auth.user)}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BatchCard — большая карточка одного item'а
// ---------------------------------------------------------------------------

function BatchCard({ item }: { item: ReviewItem }) {
  const payload = (item.payload ?? {}) as PayloadShape;
  const sourceAbbr =
    payload.source_abbreviation ?? item.source_text ?? "—";
  const sourceExpansion =
    payload.source_expansion ?? payload.surface ?? item.candidate_text ?? "";
  const targetAbbr = payload.target_abbreviation;
  const targetExpansion =
    payload.target_expansion ??
    item.canonical_text ??
    item.candidate_text ??
    "";

  const examples = useMemo(
    () => normalizeExamples(payload.examples ?? []),
    [payload.examples],
  );
  const disambig = payload.disambiguation ?? [];

  return (
    <div className="rb-card">
      {/* Card head */}
      <div className="rb-card__head">
        <span className={`mt-itemtype mt-itemtype--${item.item_type}`}>
          {itemTypeLabel(item.item_type)}
        </span>
        {item.domain && (
          <span className="mt-mono mt-text-tight" style={{ color: "var(--muted-strong)" }}>
            domain:{" "}
            <strong style={{ color: "var(--text-soft)" }}>{item.domain}</strong>
          </span>
        )}
        {item.confidence != null && (
          <span className="mt-mono mt-text-tight" style={{ color: "var(--muted-strong)" }}>
            conf:{" "}
            <strong style={{ color: "var(--text-soft)" }}>
              {item.confidence.toFixed(2)}
            </strong>
          </span>
        )}
        {payload.raw_occurrence_count != null && (
          <span className="mt-mono mt-text-tight" style={{ color: "var(--muted-strong)" }}>
            occurrences:{" "}
            <strong style={{ color: "var(--text-soft)" }}>
              {payload.raw_occurrence_count.toLocaleString("ru-RU")}
            </strong>
          </span>
        )}
        <span className="rb-card__id">{item.stable_key}</span>
      </div>

      {/* HERO */}
      <div className="rb-hero">
        <div className="rb-hero__pair">
          <div className="rb-hero__cell">
            <span className="rb-hero__lang">
              {langTagLabel(item.source_lang ?? null)} · оригинал
            </span>
            <span className="rb-hero__abbr">{sourceAbbr}</span>
          </div>
          {sourceExpansion && (
            <>
              <span className="rb-hero__arrow">→</span>
              <div className="rb-hero__cell" style={{ textAlign: "left" }}>
                <span className="rb-hero__lang">расшифровка</span>
                <span className="rb-hero__expand">{sourceExpansion}</span>
              </div>
            </>
          )}
        </div>

        {(targetAbbr || targetExpansion) && (
          <>
            <span className="rb-hero__divider" />
            <div className="rb-hero__pair">
              {targetAbbr && (
                <div className="rb-hero__cell">
                  <span className="rb-hero__lang">
                    {langTagLabel(item.target_lang ?? null)} · перевод
                  </span>
                  <span
                    className="rb-hero__abbr"
                    style={{ fontSize: 36 }}
                  >
                    {targetAbbr}
                  </span>
                </div>
              )}
              {targetAbbr && targetExpansion && (
                <span className="rb-hero__arrow">→</span>
              )}
              {targetExpansion && (
                <div className="rb-hero__cell" style={{ textAlign: "left" }}>
                  <span className="rb-hero__lang">
                    {!targetAbbr
                      ? `${langTagLabel(item.target_lang ?? null)} · перевод`
                      : "расшифровка"}
                  </span>
                  <span
                    className="rb-hero__expand"
                    style={{ fontSize: 22 }}
                  >
                    {targetExpansion}
                  </span>
                </div>
              )}
            </div>
          </>
        )}

        {payload.is_ambiguous && (
          <div style={{ textAlign: "center", marginTop: 22 }}>
            <div className="rb-flag">
              <span className="rb-flag__sigil">±</span>
              <span>
                <strong>Ambiguous</strong> — несколько sense'ов в корпусе
              </span>
            </div>
          </div>
        )}
      </div>

      {/* EVIDENCE — два столбца */}
      {(disambig.length > 0 || examples.length > 0) && (
        <div className="rb-evidence">
          <div>
            <div className="rb-evtitle">
              <span>Disambiguation</span>
              {disambig.length > 0 && (
                <span
                  className="mt-mono mt-text-tight"
                  style={{
                    fontWeight: "var(--fw-medium)",
                    textTransform: "none",
                    letterSpacing: 0,
                    color: "var(--muted)",
                  }}
                >
                  {disambig.length} candidates
                </span>
              )}
            </div>
            {disambig.length === 0 ? (
              <div className="mt-text-tight mt-muted">нет данных</div>
            ) : (
              disambig.map((d, i) => (
                <div
                  key={i}
                  className={"rb-sense" + (d.is_chosen ? " is-chosen" : "")}
                >
                  <div className="rb-sense__name">
                    {d.name}
                    {d.is_chosen && (
                      <span className="mt-badge mt-badge--success mt-badge--no-dot">
                        chosen
                      </span>
                    )}
                  </div>
                  <span className="rb-sense__score">{d.score.toFixed(2)}</span>
                  {d.sub && <span className="rb-sense__sub">{d.sub}</span>}
                  <div className="rb-sense__bar">
                    <i
                      style={{ width: `${Math.min(100, d.score * 100)}%` }}
                    />
                  </div>
                </div>
              ))
            )}
          </div>

          <div>
            <div className="rb-evtitle">
              <span>Usage in source</span>
              {examples.length > 0 && (
                <span
                  className="mt-mono mt-text-tight"
                  style={{
                    fontWeight: "var(--fw-medium)",
                    textTransform: "none",
                    letterSpacing: 0,
                    color: "var(--muted)",
                  }}
                >
                  {Math.min(2, examples.length)} of {examples.length}
                </span>
              )}
            </div>
            {examples.length === 0 ? (
              <div className="mt-text-tight mt-muted">нет примеров</div>
            ) : (
              examples.slice(0, 2).map((ex, i) => (
                <div className="rb-example" key={i}>
                  {ex.text}
                  {ex.source && (
                    <span className="rb-example__src">{ex.source}</span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function normalizeExamples(
  examples: Array<string | Record<string, unknown>>,
): Array<{ text: string; source?: string }> {
  return examples.map((ex) => {
    if (typeof ex === "string") return { text: ex };
    const v = ex as Record<string, unknown>;
    return {
      text: String(v.text ?? v.context ?? v.snippet ?? v.fragment ?? ""),
      source: (v.source ?? v.source_name ?? v.url) as string | undefined,
    };
  });
}

function pegIconClass(d: DecisionVerb): string {
  switch (d) {
    case "approve":
    case "apply_draft":
      return "rb-peg__icon--approve";
    case "edit_and_approve":
      return "rb-peg__icon--edit";
    case "reject":
      return "rb-peg__icon--reject";
    case "needs_second_review":
    case "needs_review":
    case "mark_ambiguous":
      return "rb-peg__icon--second";
    default:
      return "rb-peg__icon--skip";
  }
}

function pegIcon(d: DecisionVerb): string {
  switch (d) {
    case "approve":
    case "apply_draft":
      return "✓";
    case "edit_and_approve":
      return "✎";
    case "reject":
      return "✗";
    case "needs_second_review":
    case "needs_review":
      return "2";
    default:
      return "⤼";
  }
}

function formatAgo(ts: number): string {
  const diff = Date.now() - ts;
  const sec = Math.round(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  return `${Math.round(min / 60)}h ago`;
}

// targetLabelForItem / BatchEditField удалены — Stage 4 переиспользует
// type-specific формы (RecordFormSwitch).
// fieldOverridesForItem вынесен в ./components/recordForms/shared.ts.
